import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FilterService} from "../../../shared/services/filter.service";
import {ActivatedRoute, Router} from "@angular/router";
import {ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {AuthService} from "../../../shared/services/auth.service";
import {WarehouseProduct} from "../../../shared/interface/warehouse-product";
import {delay, distinctUntilChanged, takeUntil, tap} from "rxjs/operators";
import {CountedAndPaginated} from "../../../shared/interface/response/counted-and-paginated";
import {WarehouseService} from "../../../shared/services/warehouse.service";
import {NgForm} from "@angular/forms";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {Profile} from "../../../shared/interface/profile";
import {select, Store} from "@ngrx/store";
import {getCurrentRouteState} from "../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../shared/ngrx/route-serializer";
import {StoreRootState} from "../../../shared/ngrx/reducers";
import {
  WarehouseProductAddDialogComponent
} from "../warehouse-product-add-dialog/warehouse-product-add-dialog.component";
import {
    DYNAMIC_DICTIONARY_CATEGORY,
    DYNAMIC_DICTIONARY_WAREHOUSE,
} from "../../../shared/services/dict.service";

@Component({
  selector: 'app-all-warehouse-product-list',
  templateUrl: './all-warehouse-product-list.component.html',
  styleUrls: ['./all-warehouse-product-list.component.scss']
})
export class AllWarehouseProductListComponent implements AfterViewInit, OnDestroy{
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() embeddedView = false;
  @Output() changed: EventEmitter<boolean> = new EventEmitter<boolean>();

  readonly DYNAMIC_DICTIONARY_CATEGORY = DYNAMIC_DICTIONARY_CATEGORY;
  readonly DYNAMIC_DICTIONARY_WAREHOUSE = DYNAMIC_DICTIONARY_WAREHOUSE;

  initialFilters: any;
  userPermissions: ReplaySubject<UserPermissions>;
  warehouseProducts: WarehouseProduct[] = [];
  totalData = 0;
  sortBy = null;
  sortOrder = null;
  profile: Profile;

  displayedColumns: string[] = [
    'image',
    'id',
    'productName',
    'productAmount',
    'category',
    'protocol',
    'warehouse',
    'company',
  ];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);


  constructor(
    private dialog: MatDialog,
    private warehouseService: WarehouseService,
    private snack: MatSnackBar,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private auth: AuthService,
    private router: Router,
    private store: Store<StoreRootState>,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.warehouseService.getProductsListSubject()
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<WarehouseProduct>) => {
          this.warehouseProducts = [...e.items];
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();

  }

  ngAfterViewInit(): void {
    this.auth.getProfile()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((profile: Profile) => {
        this.profile = profile;
      });
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((event: PageEvent) => {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          ...this.filtersForm.value
        })]);
      });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$)
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.loadListData();
      });
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      recordId: 'string',
      category: 'int',
      contains$productName: 'string',
      gte$productAmount: 'int',
      lte$productAmount: 'int',
      gte$productPrice: 'int',
      lte$productPrice: 'int',
      gte$purchaseDate: 'date',
      lte$purchaseDate: 'date',
      gte$expirationDate: 'date',
      lte$expirationDate: 'date',
      warehouse: 'int',
      company: 'int',
      protocol: 'int',
    }, filters);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  filter(): void {
    const formValues = {...this.filtersForm.value};
    for (const key in formValues) {
      if (formValues[key] === undefined) {
        delete formValues[key];
      }
    }

    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path,
      this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...formValues
      })
    ]);
  }


  resetFilters(): void {
    this.filtersForm.resetForm();

    if (this.embeddedView) {
      this.loadListData();
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
        page: 0,
        pageSize: this.paginator.pageSize,
      }]);
    }
  }

  loadListData(): void {
    this.warehouseService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.initialFilters,
    );
  }

  downloadReport(): void {
    this.warehouseService.downloadReport(this.filtersForm.value)
      .subscribe((response: Uuid) => {
          this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: { jobId: response.uuid, showProgress: false },
          });
        },
        (error: HttpErrorResponse) => {
          this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), { duration: 2000 });
        }
      );
  }

  addProduct(): void {
    const dialogRef = this.dialog.open(WarehouseProductAddDialogComponent, {
      width: '90%',
      maxHeight: '90%',
      panelClass: 'full-height-dialog',
    });

    dialogRef.afterClosed().subscribe(result => {
      this.loadListData();
      this.changed.emit(true);
      this.dialog.closeAll();
    });
  }
  }
